import axios from 'axios';
import * as Sentry from '@sentry/react';
import i18n from '../i18n';
import { installFetchLanguage, withLanguageHeader } from './requestLanguage';
import { isNativeApp } from './native';
import { renewWithDeviceKey } from './deviceSession';
import { detailText } from './apiError';
import { markSessionExpired } from './sessionRefresh';

let toastContext = null;

export const setToastContext = (context) => {
  toastContext = context;
};

// Add request interceptor to automatically add token to all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Backend messages (errors, activity log, notifications) follow the UI
    // language. Covers the supplier and customer portals too (same axios).
    return withLanguageHeader(config, i18n);
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Pages that call the API with fetch() send the UI language as well.
installFetchLanguage(i18n);

// Queue for errors before toast is ready
const errorQueue = [];

// Cache to prevent duplicate error messages
const recentErrors = new Map();
const ERROR_COOLDOWN = 3000; // 3 seconds cooldown for same error

const shouldShowError = (message) => {
  const now = Date.now();
  const lastShown = recentErrors.get(message);
  
  if (lastShown && now - lastShown < ERROR_COOLDOWN) {
    return false; // Don't show, too recent
  }
  
  recentErrors.set(message, now);
  
  // Clean up old entries after 50 items
  if (recentErrors.size > 50) {
    const oldestKey = recentErrors.keys().next().value;
    recentErrors.delete(oldestKey);
  }
  
  return true;
};

const showError = (message, duration = 5000) => {
  if (!shouldShowError(message)) return;
  
  if (toastContext) {
    toastContext.error(message, duration);
  } else {
    errorQueue.push({ message, type: 'error', duration });
  }
};

const showWarning = (message, duration = 5000) => {
  if (!shouldShowError(message)) return;
  
  if (toastContext) {
    toastContext.warning(message, duration);
  } else {
    errorQueue.push({ message, type: 'warning', duration });
  }
};

// Process queued errors when toast becomes available
export const setToastContextWithQueue = (context) => {
  toastContext = context;
  // Show any queued errors
  errorQueue.forEach(({ message, type, duration }) => {
    if (type === 'error') {
      context.error(message, duration);
    } else if (type === 'warning') {
      context.warning(message, duration);
    }
  });
  errorQueue.length = 0; // Clear queue
};

// Mobile app: a 401 first tries to renew the session with the device key
// (utils/deviceSession) and replays the request once; only a refused key
// falls through to the normal "session expired" logout below.
const canRetryWithDeviceKey = (error) => {
  const config = error.config;
  return error.response?.status === 401 && isNativeApp() && config && !config._deviceRetried
    && !/\/auth\/(login|device-)/.test(config.url || '');
};

const retryWithDeviceKey = (error) => renewWithDeviceKey().then((fresh) => {
  if (!fresh) return handleResponseError(error);
  axios.defaults.headers.common['Authorization'] = `Bearer ${fresh}`;
  const config = { ...error.config, _deviceRetried: true };
  config.headers = { ...config.headers, Authorization: `Bearer ${fresh}` };
  return axios(config);
});

// Add a response interceptor to handle errors globally
axios.interceptors.response.use(
  (response) => response,
  (error) => (canRetryWithDeviceKey(error) ? retryWithDeviceKey(error) : handleResponseError(error))
);

function handleResponseError(error) {
    if (error.response) {
      const { status, data } = error.response;

      // A 422 detail is a list of {loc, msg}; pages put `detail` straight
      // into toasts/JSX, which crashed React. Hand them readable text and
      // keep the original list for callers that want the fields.
      if (status === 422 && data && typeof data === 'object' && data.detail && typeof data.detail !== 'string') {
        data.detail_list = data.detail;
        data.detail = detailText(data.detail) || i18n.t('shared.http.invalidFields', { fields: '' });
      }
      
      // Handle 403 Forbidden - Access Denied
      if (status === 403) {
        // Structured upsell case: detail is {code:'module_not_licensed',module:'sales'}.
        // Backend (#requireModuleAccess) emits this when a tenant tries to use a
        // feature whose module isn't on their license. Surface a CTA toast that
        // links to /checkout with the module pre-checked, instead of a generic
        // 'access denied' that leaves the user dead-ended.
        if (data?.detail && typeof data.detail === 'object' && data.detail.code === 'module_not_licensed') {
          const moduleKey = data.detail.module;
          const message = i18n.t('shared.http.moduleNotLicensed', { module: moduleKey });
          if (shouldShowError(message)) {
            if (toastContext?.warning) {
              toastContext.warning(message, 8000, {
                action: {
                  label: i18n.t('shared.http.addNow'),
                  onClick: () => {
                    window.location.href = `/checkout?prefill_modules=${encodeURIComponent(moduleKey)}`;
                  },
                },
              });
            } else {
              // No toast yet — fall back to confirm so the user still has a path.
              if (window.confirm(`${message}\n\n${i18n.t('shared.http.goToCheckout')}`)) {
                window.location.href = `/checkout?prefill_modules=${encodeURIComponent(moduleKey)}`;
              }
            }
          }
        } else if (error.response.headers?.['x-error-code'] === 'user_limit_reached') {
          // Every seat of the plan is taken: say so and link to the billing page.
          const message = (typeof data?.detail === 'string' ? data.detail : null) || i18n.t('shared.http.userLimitReached');
          if (shouldShowError(message)) {
            const action = {
              label: i18n.t('shared.http.upgradeSeats'),
              onClick: () => { window.location.href = '/modules/admin/billing?action=add-seats'; },
            };
            if (toastContext) toastContext.error(message, 10000, { action });
            else errorQueue.push({ message, type: 'error', duration: 10000 });
          }
        } else {
          const message = (typeof data?.detail === 'string' ? data.detail : null) || i18n.t('shared.http.forbidden');
          showError(message);
        }
      }
      
      // Handle 401 Unauthorized
      else if (status === 401) {
        // Only redirect to login if:
        // 1. It's not the initial login attempt (failed login)
        // 2. User has a token (meaning they were logged in)
        const isLoginAttempt = error.config.url?.includes('/auth/login');
        const hasToken = localStorage.getItem('token');
        
        if (!isLoginAttempt && hasToken) {
          // an admin made the account passive: say so, not "session expired"
          const inactive = error.response.headers?.['x-account-status'] === 'inactive' && typeof data?.detail === 'string';
          const message = inactive ? data.detail : i18n.t('shared.http.sessionExpired');
          showWarning(message);
          markSessionExpired();
          // Supplier portal sessions go back to the supplier login (e.g.
          // after the password was changed or reset elsewhere).
          let loginPath = '/login';
          try {
            if (JSON.parse(localStorage.getItem('user') || '{}').role === 'supplier_user') {
              loginPath = '/supplier/login';
            }
          } catch (e) { /* malformed user blob: default login */ }

          // Clear stored auth data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          
          // Redirect to login after a delay (only once)
          if (!window.redirectingToLogin) {
            window.redirectingToLogin = true;
            setTimeout(() => {
              window.location.href = loginPath;
              window.redirectingToLogin = false; // Reset for next time
            }, 2000);
          }
        } else if (isLoginAttempt) {
          // Don't show toast for failed login attempts - let the form handle it
          console.log('Login failed - invalid credentials');
        }
      }
      
      // Handle 404 Not Found (but don't show for all 404s)
      else if (status === 404 && !error.config.url?.includes('/api/')) {
        const message = data?.detail || i18n.t('shared.http.notFound');
        showWarning(message, 4000);
      }
      
      // Handle 500 Server Error. A 503 that says why (a feature switched off,
      // e.g. the bank integration pilot) is the page's own message, shown
      // once by the page (R5H-09: two toasts).
      else if (status >= 500 && !(status === 503 && typeof data?.detail === 'string')) {
        const message = i18n.t('shared.http.serverError');
        showError(message);
      }

      // Send 5xx (and unexpected 4xx like 429) to Sentry so backend regressions
      // surface in the dashboard instead of just popping a toast that the user
      // dismisses. 401/403/404 are user-action noise — skip them.
      if (status >= 500 || status === 429) {
        Sentry.withScope((scope) => {
          scope.setLevel('error');
          scope.setTag('http.status', String(status));
          scope.setTag('http.method', error.config?.method?.toUpperCase() || 'GET');
          scope.setContext('request', {
            url: error.config?.url,
            method: error.config?.method,
            status,
            response_data: typeof data === 'object' ? JSON.stringify(data).slice(0, 500) : String(data).slice(0, 500),
          });
          Sentry.captureException(error);
        });
      }
    } else if (error.request && !error.message.includes('Network Error')) {
      // Network error - no response received
      const message = i18n.t('shared.http.networkError');
      showError(message);
      // Network failures matter too — usually means deploy in progress, CDN
      // cold start, or actual outage. Sample heavily to avoid quota burn.
      if (Math.random() < 0.1) {
        Sentry.captureException(error, { tags: { network_error: 'true' } });
      }
    }

    return Promise.reject(error);
}

export default axios;
